/**
 * StoryboardDesignerNode (Canvas V2) — Text-Centric
 *
 * Displays storyboard beat descriptions organized by grid sections.
 * Each grid (3×3, 2×2, 1×1) is shown as a collapsible section with
 * per-panel rows showing panel number, position label, and beat text.
 *
 * Width: 480px fixed. UnifiedBottomPanel: Config only.
 */

import { memo, useState, useMemo, useCallback, useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent, type WheelEvent as ReactWheelEvent } from 'react'
import { NODE_CARD_RADIUS_CLASS } from '../shared/constants'
import { useDuplicateMenuItems } from '../shared/useDuplicateMenuItems'
import { useTranslation } from 'react-i18next'
import { Handle, Position, type NodeProps, useReactFlow, useStore } from 'reactflow'
import { BookmarkPlus, Check, ChevronDown, ChevronRight, Expand, Loader2, SlidersHorizontal } from 'lucide-react'
import { AddToChatIcon } from '../shared/AddToChatIcon'
import { NodeStatusMessage } from '../shared/NodeStatusMessage'
import { executeNode, cancelNodeExecution, InsufficientSparksError } from '../../../../api/client'
import { useClickLock } from '../../../../hooks/useClickLock'
import { useBooleanFlagValue } from '../../../../hooks/useBooleanFlagValue'
import { useShowInsufficientSparks } from '../../../../contexts/InsufficientSparksContext'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { useActiveCanvasId } from '../../../../contexts/ActiveCanvasIdContext'
import { useCanvasOptional } from '../../CanvasContext'
import { useCanvasEdges } from '../../CanvasEdgesContext'
import { useHandlesVisibleForNode } from '../../HandleVisibilityContext'
import { useCanvasNodeChromeMode } from '../../CanvasNodeChromeModeContext'
import { useCanvasNode } from '../../../../hooks/useCanvasNode'
import { useStyleLabel } from '../../../../hooks/useStyleLabel'
import { dataStatus, dataStrOpt, type ReactFlowCanvasEdgeData, ReactFlowCanvasNodeData } from '../../../../hooks/useYCanvas'
import { createDebug, createNadDebug } from '../../../../utils/debugLog'
import { sanitizeNodeError } from '../../../../utils/sanitizeNodeError'
import { getImageModelLabel } from '../../../../constants/modelAliases'
import {
  AgentNodeToolbar,
  FloatingNodeLabel,
  UnifiedBottomPanel,
  DEFAULT_TAB,
  ConfigTabContent,
  FlatIdleNodeCard,
  useNodeActions,
  useSaveToLibrary,
  useBottomPanelAutoOpen,
  useSelectSingleCanvasNode,
  type TabDefinition,
  type ToolbarAction,
  type SelectedReference,
  buildAssetReferencesFromEdges,
  buildImageReferenceEdgeUpdates,
  getImageOutputPort,
  createHandlesFromSchema,
  encodeNodeError,
  resolveNodeErrorMessage,
} from '../shared'
import { localizeDefaultNodeName } from '../shared/defaultNodeNames'
import { NODE_SCHEMAS_BY_TYPE, bindCanvasNodeByFeType, type ReactFlowCanvasNodeDataByType, type ShotFrameLinkDict, type ShotListRowDict, type ShotVideoPackDict } from '../../../../generated/nodeDataTypes'
import { getThumbnailUrl } from '../../../../api/useAssetUrl'
import { generatedImagePath, getPickedIndex } from '../shared/designerUtils'
import { buildGridSections, positionLabelKey, type GridSection } from './gridDecomposition'
import { ShotListTable, ShotOutgoingTransition, ShotVisualDescription, resolveShotFramePreview, type FramingField, type ShotFrameRole, type ShotFrameTarget, type ShotListEmptyState } from './ShotListTable'
import { openingTransition, outgoingTransition } from './shotListPresentation'
import { projectGeneratedShots } from './projectGeneratedShots'
import { ShotEditError, durationSecondsFromMs, readShotField, validateShotEdit, writeShotField, type ShotEdit, type ShotEditField, type ShotEditSource } from './shotListEdits'
import { SHOT_LIST_NODE_DIMS } from './shotListLayout'
import { ExpandedShotListModal } from './ExpandedShotListModal'
import { screenplayCanonicalJSON, screenplayFingerprint } from './screenplayFingerprint'
import { trackGenerationCancelled } from '../../../../analytics/interactionAnalytics'
import { useNuxTooltipEvents } from '../../../../onboarding/nuxEvents'
import { track } from '../../../../analytics/events'

const debug = createDebug('dw:storyboard-designer:node')
const textUiLog = createNadDebug('dw:storyboard-text-ui')

const NODE_WIDTH = 480
const FRAME_ROLES: readonly ShotFrameRole[] = ['start', 'key', 'end']
const SHOT_LIST_VIEWS: readonly ('shot-list' | 'storyboard')[] = ['shot-list', 'storyboard']
const storyboardDesignerSchema = NODE_SCHEMAS_BY_TYPE.storyboardDesigner
type StoryboardDesignerNodeData = ReactFlowCanvasNodeDataByType['storyboardDesigner']
type StoryboardDesignerConfigUpdateKey = 'in_script' | 'in_style' | 'in_model'
type FrameEdit = { shotId: string; role: ShotFrameRole; before?: { imageNodeId: string; origin: 'user' | 'director' }; after?: string }

/** Config-only tab list (no variants, no refine). Label translated at render site. */
const CONFIG_TABS_STATIC: TabDefinition[] = [
  { id: 'config', label: 'Config', icon: SlidersHorizontal },
]

const HANDLES = createHandlesFromSchema(storyboardDesignerSchema, {
  inputDefaults: { position: Position.Top },
  outputDefaults: { position: Position.Bottom },
  inputs: {
    in_script: { position: Position.Left },
    in_image: { position: Position.Left, style: { top: '68%' } },
    in_characters: { style: { left: '30%' } },
    in_character_names: { style: { left: '40%' } },
    in_locations: { style: { left: '50%' } },
    in_location_names: { style: { left: '60%' } },
    in_objects: { style: { left: '70%' } },
    in_object_names: { style: { left: '80%' } },
  },
  outputs: {
    out_image: { position: Position.Right },
    out_beats: { style: { left: '40%' } },
    out_panel_refs: { style: { left: '60%' } },
    out_metadata: { position: Position.Right, style: { top: '65%' } },
  },
})

// ── Grid Section Component ──────────────────────────────────────────────

/**
 * Localize a grid position label ("top-left", "center", …) for display.
 *
 * The stored value stays canonical English — the backend uses the same
 * vocabulary in the IMAGE prompt (`_9GRID_LABELS` in
 * `storyboard_designer/agent.py`), so only the rendering changes. An
 * unrecognized value (the `panel-N` fallback) renders verbatim.
 */
function localizePositionLabel(
  label: string,
  t: (key: string, opts?: { defaultValue?: string }) => string,
): string {
  const key = positionLabelKey(label)
  if (!key) return label
  return t(`storyboardDesignerNode.position.${key}`, { defaultValue: label })
}

const GridSectionView = memo(function GridSectionView({
  section,
  defaultExpanded,
}: {
  section: GridSection
  defaultExpanded: boolean
}) {
  const { t } = useTranslation('canvas')
  const [expanded, setExpanded] = useState(defaultExpanded)
  const [rows, cols] = section.size
  const gridLabel = t('storyboardDesignerNode.gridLabel', { index: section.gridIndex + 1, rows, cols })

  return (
    <div className="border border-border-subtle/50 rounded-md overflow-hidden bg-surface-0/50">
      {/* Grid header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center gap-1.5 px-3 py-1.5 text-left hover:bg-surface-1 transition-colors nodrag"
      >
        {expanded ? (
          <ChevronDown className="w-3 h-3 text-foreground/40 shrink-0" />
        ) : (
          <ChevronRight className="w-3 h-3 text-foreground/40 shrink-0" />
        )}
        <span className="text-xxs font-bold text-foreground/70 uppercase tracking-wider">
          {gridLabel}
        </span>
        <span className="text-xxs text-foreground/30 ml-auto">
          {t('storyboardDesignerNode.panelCount', { count: section.panels.length })}
        </span>
      </button>

      {/* Panel rows */}
      {expanded && (
        <div className="border-t border-border-subtle/50">
          {section.panels.map((panel, i) => (
            <div
              key={panel.panelNumber}
              className={`flex gap-2.5 px-3 py-1.5 ${
                i < section.panels.length - 1 ? 'border-b border-border-subtle/30' : ''
              }`}
            >
              {/* Panel number + position label */}
              <div className="shrink-0 w-[90px] pt-0.5">
                <span className="text-xxs font-bold text-foreground/80 tabular-nums">
                  {panel.panelNumber}
                </span>
                <span className="text-xxs text-foreground/30 ml-1.5">
                  {localizePositionLabel(panel.positionLabel, t)}
                </span>
              </div>
              {/* Beat description */}
              <div className="flex-1 min-w-0">
                {panel.beat ? (
                  <p className="text-xxs text-foreground/70 leading-relaxed whitespace-pre-wrap break-words">
                    {panel.beat}
                  </p>
                ) : (
                  <span className="text-xxs text-foreground/20 italic">—</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
})

// ── Main Node Component ─────────────────────────────────────────────────

const StoryboardDesignerNode = memo(function StoryboardDesignerNode(
  props: NodeProps<StoryboardDesignerNodeData>
) {
  const nodePresentationWidth = useNodePresentationWidth()
  const { t } = useTranslation('canvas')
  const { id, data, selected, dragging } = props
  const { emitNuxEvent } = useNuxTooltipEvents()
  const status = dataStatus(data)
  const error = dataStrOpt(data, 'error')
  const cacheKey = dataStrOpt(data, 'cacheKey')
  const progressMessage = dataStrOpt(data, 'progressMessage')
  const projectId = useProjectId()
  const canvasId = useActiveCanvasId()
  const { setNodes, getNodes, fitView } = useReactFlow<ReactFlowCanvasNodeData, ReactFlowCanvasEdgeData>()
  const edges = useCanvasEdges()
  const handlesVisible = useHandlesVisibleForNode(id)
  const canvas = useCanvasOptional()
  const { lightweightNodeChrome } = useCanvasNodeChromeMode()
  const node = useCanvasNode(id, 'storyboardDesigner', data)
  const isTextShotList = props.type === 'storyboardDesigner' && node?.get('storyboard_mode') === 'text'
  const productionPlanningEnabled = useBooleanFlagValue('shot_list_production_planning_enabled', false)
  const shotListVideoEnabled = useBooleanFlagValue('shot_list_video_generation_enabled', false)
  const isShotList = isTextShotList || (props.type === 'storyboardDesigner' && node?.get('storyboard_view') === 'shot_list')
  const exactShotCount = node?.get('in_shot_count')
  const [shotCountDraft, setShotCountDraft] = useState<string | null>(null)
  const [frameMode, setFrameMode] = useState<'key' | 'start_end' | 'all'>('key')
  const [framePickerActive, setFramePickerActive] = useState(false)
  const shotListValues = node?.get('shot_list')
  const generatedShots = node?.get('out_shots') ?? []
  if (isTextShotList && generatedShots.length) {
    const measured = generatedShots[0].generation_id?.startsWith('video-shot-list:') ?? false
    if (generatedShots.some(shot => !shot.generation_id || shot.generation_id.startsWith('video-shot-list:') !== measured) ||
        (measured && node?.get('shot_list_planner_version') === 2)) {
      throw new Error('Shot List source is ambiguous: mixed video and text planner generations')
    }
  }
  const shotEditSource: ShotEditSource = !isTextShotList ? 'flat'
    : generatedShots[0]?.generation_id?.startsWith('video-shot-list:') ? 'video'
    : node?.get('shot_list_planner_version') === 2 ? 'guided' : 'screenplay'
  const guidanceWarning = isTextShotList ? node?.get('shot_guidance_warning') : undefined
  const videoGenerationPlan = isTextShotList ? node?.get('out_video_generation_plan') : undefined
  const plannedPacks = videoGenerationPlan?.packs ?? []
  const plannedShotIds = plannedPacks.flatMap(pack => pack.shot_ids ?? [])
  const videoPlanStale = Boolean(videoGenerationPlan && (
    videoGenerationPlan.source_generation_id !== generatedShots[0]?.generation_id ||
    plannedShotIds.length !== generatedShots.length ||
    plannedShotIds.some((shotId, index) => shotId !== generatedShots[index]?.id) ||
    plannedPacks.reduce((total, pack) => total + (pack.duration_ms ?? 0), 0) !==
      generatedShots.reduce((total, shot) => total + (shot.duration_ms ?? 0), 0)
  ))
  const videoPacks: ShotVideoPackDict[] = videoGenerationPlan && !videoPlanStale
    ? (videoGenerationPlan.packs ?? []).map(pack => {
        const { id: packId, generation_id, shot_ids, duration_ms, mode, model, resolution, references, exact_first, exact_last, predecessor_id, cut_reason, video_node_id } = pack
        if (!packId || generation_id === undefined || !shot_ids || duration_ms === undefined || !mode || !model || !resolution || !references || !cut_reason || !video_node_id) {
          throw new Error(`Shot List ${id} has an incomplete video generation pack`)
        }
        return { id: packId, generation_id, shot_ids, duration_ms, mode, model, resolution, references, exact_first, exact_last, predecessor_id, cut_reason, video_node_id }
      })
    : []
  const productionPlan = isTextShotList ? node?.get('out_production_plan') : undefined
  const screenplaySourceId = edges.find(edge => edge.target === id && edge.targetHandle === 'in_script'
    && edge.sourceHandle === 'out_script')?.source ?? ''
  const screenplaySourceData = useStore(state => state.nodeInternals.get(screenplaySourceId)?.data)
  const screenplaySource = useCanvasNode(screenplaySourceId, 'scriptWriter', screenplaySourceData)
  const sourceScript = screenplaySource?.get('out_script')
  const sourceScreenplay = screenplaySource?.get('out_screenplay')
  const canonicalSource = productionPlan && sourceScript && sourceScreenplay
    ? screenplayCanonicalJSON(sourceScript, JSON.stringify(sourceScreenplay.map(scene => scene.toJSON()))) : undefined
  const [sourceFingerprint, setSourceFingerprint] = useState<{ canonical: string; hash: string } | null>(null)
  useEffect(() => {
    if (!canonicalSource) return
    let active = true
    void screenplayFingerprint(canonicalSource).then(hash => {
      if (active) setSourceFingerprint({ canonical: canonicalSource, hash })
    }).catch(error => {
      if (active) { setSourceFingerprint(null); console.error('Could not verify Shot List screenplay', error) }
    })
    return () => { active = false }
  }, [canonicalSource])
  const currentScreenplayFingerprint = sourceFingerprint && sourceFingerprint.canonical === canonicalSource
    ? sourceFingerprint.hash : undefined
  const generatedSeams = node?.get('out_shot_seams') ?? []
  const shotBoundaryTypes = Object.fromEntries(generatedShots.flatMap((shot, index) => {
    const boundary = generatedSeams[index + 1]?.boundary_type
    return shot.id && boundary ? [[shot.id, boundary]] : []
  }))
  const frameLinks: ShotFrameLinkDict[] = (isTextShotList ? node?.get('out_frame_links') ?? [] : []).map(link => {
    const { version, shot_id, shot_index, role, node_id, status, source_fingerprint, error } = link
    if (version !== 1 || !shot_id || shot_index === undefined || !role || !node_id || !status || !source_fingerprint) {
      throw new Error(`Invalid Shot List frame link on node ${id}`)
    }
    return { version, shot_id, shot_index, role, node_id, status, source_fingerprint, error }
  })
  const frameColumns = isTextShotList ? FRAME_ROLES : []
  const userKeyRelinks = isTextShotList ? node?.get('shot_key_frame_relinks') ?? [] : []
  const videoAnchors = isTextShotList ? node?.get('in_video_anchors') ?? [] : []
  const allImageNodeIds = useStore(state => Array.from(state.nodeInternals.values())
    .filter(other => other.type === 'image' || other.type === 'imageGenerator').map(other => other.id).join('|'))
  const frameNodeIds = [...frameLinks.map(link => link.node_id), ...userKeyRelinks.map(item => item.image),
    ...videoAnchors.map(item => item.image), ...(framePickerActive ? allImageNodeIds.split('|') : [])].join('|')
  // Child image nodes may arrive after their frame links; subscribe to structural
  // changes so their Yjs observers are installed as soon as they appear.
  const availableFrameNodeIds = useStore(state => frameNodeIds.split('|')
    .filter(nodeId => nodeId && state.nodeInternals.has(nodeId)).join('|'))
  useEffect(() => {
    if (!isTextShotList || !canvas?.getYNode) return
    const nodes = availableFrameNodeIds.split('|').filter(Boolean).map(nodeId => canvas.getYNode?.(nodeId)).filter(node => node != null)
    const onVariantChanged = () => refreshShotList(value => value + 1)
    nodes.forEach(imageNode => imageNode.observeDeep(onVariantChanged))
    return () => nodes.forEach(imageNode => imageNode.unobserveDeep(onVariantChanged))
  }, [canvas, availableFrameNodeIds, isTextShotList])
  const [shotListView, setShotListView] = useState<'shot-list' | 'storyboard'>('shot-list')
  const [shotListExpanded, setShotListExpanded] = useState(false)
  const portraitFrames = node?.get('in_aspect_ratio') === '9:16'
  const [, refreshShotList] = useState(0)
  useEffect(() => {
    if (!isShotList) return
    const raw = canvas?.getYNode?.(id)
    if (!raw) return
    const onNestedEdit = () => refreshShotList(value => value + 1)
    raw.observeDeep(onNestedEdit)
    return () => raw.unobserveDeep(onNestedEdit)
  }, [canvas, id, isShotList])
  const shotEditHistory = useRef<{ undo: (ShotEdit | FrameEdit)[]; redo: (ShotEdit | FrameEdit)[] }>({ undo: [], redo: [] })
  const trackShotEdit = (action: 'cell_commit' | 'frame_relink' | 'undo' | 'redo', field?: ShotEditField, role?: ShotFrameRole) => {
    if (!projectId) return
    track('canvas_shot_list_edit_action', {
      project_id: projectId, node_id: id, shot_list_edit_action: action,
      shot_list_edit_source: shotEditSource,
      ...(field ? { shot_list_edit_field: field } : {}),
      ...(role ? { shot_list_edit_role: role } : {}),
    })
  }
  const shotRows: ShotListRowDict[] = isTextShotList ? projectGeneratedShots(generatedShots, generatedSeams) : (shotListValues ?? []).map(row => {
    if (!row.id || row.duration_ms === undefined || row.visual_description === undefined) {
      throw new Error('Shot-list row is missing id, duration_ms or visual_description')
    }
    return {
      id: row.id, duration_ms: row.duration_ms, visual_description: row.visual_description,
      scene_ordinal: row.scene_ordinal,
      shot_number: row.shot_number, location: row.location,
      framing_shot: row.framing_shot, framing_angle: row.framing_angle,
      framing_lens: row.framing_lens, framing_focus: row.framing_focus,
      camera_movement: row.camera_movement, transition_in: row.transition_in,
      transition_out: row.transition_out, dialogue_voiceover: row.dialogue_voiceover, sfx: row.sfx,
    }
  })
  const imagePath = (imageNodeId: string): string | undefined => {
    const match = getNodes().find(other => other.id === imageNodeId)
    const raw = canvas?.getYNode?.(imageNodeId)
    if (!raw || !match || (match.type !== 'image' && match.type !== 'imageGenerator')) return undefined
    const output = match.type === 'image' ? bindCanvasNodeByFeType(raw, 'image').get('out_image')
      : bindCanvasNodeByFeType(raw, 'imageGenerator').get('out_image')
    return generatedImagePath(output?.history?.[getPickedIndex(output)])
  }
  const imagePreview = (imageNodeId: string): string | undefined => {
    const path = imagePath(imageNodeId)
    return path && projectId ? getThumbnailUrl(projectId, path, 300, cacheKey) ?? undefined : undefined
  }
  const frameChoices = (framePickerActive ? allImageNodeIds.split('|') : []).filter(imageNodeId => imageNodeId && imagePath(imageNodeId))
    .map(imageNodeId => ({ id: imageNodeId, label: getNodes().find(other => other.id === imageNodeId)?.data?.label?.toString() ?? imageNodeId }))
  const frameSelectionCounts = new Map<string, number>()
  for (const anchor of videoAnchors) {
    if (anchor.exact === false) continue
    const key = `${anchor.shot_id}:${anchor.role === 'first' ? 'start' : 'end'}`
    frameSelectionCounts.set(key, (frameSelectionCounts.get(key) ?? 0) + 1)
  }
  for (const relink of userKeyRelinks) {
    const key = `${relink.shot_id}:key`
    frameSelectionCounts.set(key, (frameSelectionCounts.get(key) ?? 0) + 1)
  }
  const duplicateFrameKeys = [...frameSelectionCounts].filter(([, count]) => count > 1).map(([key]) => key)
  const selectedFrames: Record<string, { imageNodeId: string; preview?: string; origin: 'user' | 'director' }> = {}
  for (const anchor of videoAnchors) {
    if (!anchor.shot_id || !anchor.role || !anchor.image || anchor.exact === false) continue
    const role = anchor.role === 'first' ? 'start' : 'end'
    const key = `${anchor.shot_id}:${role}`
    if (selectedFrames[key]) textUiLog({ event: 'shot-list:duplicate-frame-selection', feature: 'shot-list', node_id: id, shot_id: anchor.shot_id, role })
    selectedFrames[key] = { imageNodeId: anchor.image, preview: imagePreview(anchor.image), origin: anchor.origin === 'user_relink' ? 'user' : 'director' }
  }
  for (const relink of userKeyRelinks) {
    if (!relink.shot_id || !relink.image) throw new Error('Shot List has an incomplete key frame relink')
    const key = `${relink.shot_id}:key`
    if (selectedFrames[key]) textUiLog({ event: 'shot-list:duplicate-frame-selection', feature: 'shot-list', node_id: id, shot_id: relink.shot_id, role: 'key' })
    selectedFrames[key] = { imageNodeId: relink.image, preview: imagePreview(relink.image), origin: 'user' }
  }
  const hasStoryboardContent = frameLinks.length > 0 || Object.keys(selectedFrames).length > 0
  const effectiveShotListView = shotListView === 'storyboard' && !hasStoryboardContent ? 'shot-list' : shotListView
  useEffect(() => {
    if (shotListView === 'storyboard' && !hasStoryboardContent) setShotListView('shot-list')
  }, [shotListView, hasStoryboardContent])
  const changeFrameRelink = (shotId: string, role: ShotFrameRole, imageNodeId: string, options?: {
    recordHistory?: boolean; restoreDirector?: boolean
  }) => {
    assertShotEditable()
    if (!node || !canvas?.transact || !isTextShotList) throw new Error('Frame relinking requires a text Shot List')
    if (!node.get('out_shots')?.some(shot => shot.id === shotId)) throw new ShotEditError('shotList.errors.shotMissing', `Shot ${shotId} no longer exists`, { id: shotId })
    const liveChoice = role === 'key' ? node.get('shot_key_frame_relinks')?.find(item => item.shot_id === shotId) : undefined
    const liveAnchor = role !== 'key' ? node.get('in_video_anchors')?.find(item => item.shot_id === shotId &&
      item.role === (role === 'start' ? 'first' : 'last') && item.exact !== false) : undefined
    const current: FrameEdit['before'] = liveChoice?.image ? { imageNodeId: liveChoice.image, origin: 'user' }
      : liveAnchor?.image ? { imageNodeId: liveAnchor.image, origin: liveAnchor.origin === 'user_relink' ? 'user' : 'director' }
      : undefined
    const matches = role === 'key'
      ? (node.get('shot_key_frame_relinks') ?? []).filter(item => item.shot_id === shotId)
      : (node.get('in_video_anchors') ?? []).filter(item => item.shot_id === shotId &&
          item.role === (role === 'start' ? 'first' : 'last') && item.exact !== false)
    if (current?.imageNodeId === imageNodeId && matches.length === 1) return true
    if (imageNodeId) {
      if (imageNodeId === id || !imagePath(imageNodeId)) throw new ShotEditError('shotList.errors.chooseReadyImage', 'Choose a ready image node on this canvas')
      const target = getNodes().find(other => other.id === imageNodeId)
      if (!target || ['running', 'pending_execution', 'pending_cost_confirmation'].includes(dataStatus(target.data))) {
        throw new ShotEditError('shotList.errors.waitForImage', 'Wait until the selected image node is ready')
      }
    } else if (current?.origin === 'director') {
      throw new ShotEditError('shotList.errors.relinkDirectorAnchor', 'Director anchors cannot be cleared here; relink to replace this anchor')
    }
    if (current?.origin === 'director' && imageNodeId && current.imageNodeId !== imageNodeId && options?.recordHistory !== false &&
      !window.confirm(t('shotList.confirmReplaceDirectorAnchor'))) return false
    const before: FrameEdit['before'] = current ? { imageNodeId: current.imageNodeId, origin: current.origin } : undefined
    const existing = role === 'key' ? node.get('shot_key_frame_relinks') ?? [] : node.get('in_video_anchors') ?? []
    const index = role === 'key'
      ? node.get('shot_key_frame_relinks')?.findIndex(entry => entry.shot_id === shotId) ?? -1
      : node.get('in_video_anchors')?.findIndex(entry => entry.shot_id === shotId && entry.role === (role === 'start' ? 'first' : 'last') && entry.exact !== false) ?? -1
    const oldEntry = index >= 0 ? JSON.stringify(existing[index]?.toJSON()) : undefined
    canvas.transact(() => {
      assertShotEditable()
      if (imageNodeId && !imagePath(imageNodeId)) throw new ShotEditError('shotList.errors.imageNoLongerReady', 'Selected image is no longer ready')
      const live = role === 'key' ? node.get('shot_key_frame_relinks') ?? [] : node.get('in_video_anchors') ?? []
      const liveIndex = role === 'key'
        ? node.get('shot_key_frame_relinks')?.findIndex(entry => entry.shot_id === shotId) ?? -1
        : node.get('in_video_anchors')?.findIndex(entry => entry.shot_id === shotId && entry.role === (role === 'start' ? 'first' : 'last') && entry.exact !== false) ?? -1
      if (index !== liveIndex || (index >= 0 && JSON.stringify(live[liveIndex]?.toJSON()) !== oldEntry)) {
        throw new ShotEditError('shotList.errors.frameConflict', 'This frame changed elsewhere; refresh before relinking')
      }
      if (role === 'key') {
        node.replaceShotKeyFrameRelinksEntry(
          { shot_id: shotId }, imageNodeId ? { shot_id: shotId, image: imageNodeId } : undefined
        )
      } else {
        const anchorRole = role === 'start' ? 'first' : 'last'
        node.replaceInVideoAnchorsEntry(
          { shot_id: shotId, role: anchorRole },
          imageNodeId ? { shot_id: shotId, role: anchorRole, image: imageNodeId,
            exact: true, ...(options?.restoreDirector || (current?.origin === 'director' && current.imageNodeId === imageNodeId) ? {} : { origin: 'user_relink' }) } : undefined
        )
      }
      node.delete('out_video_generation_plan')
    })
    if (options?.recordHistory !== false) {
      shotEditHistory.current.undo.push({ shotId, role, before, after: imageNodeId || undefined })
      if (shotEditHistory.current.undo.length > 50) shotEditHistory.current.undo.shift()
      shotEditHistory.current.redo = []
    }
    textUiLog({ event: 'shot-list:frame-relinked', feature: 'shot-list', node_id: id, shot_id: shotId, role, has_relink: Boolean(imageNodeId) })
    if (options?.recordHistory !== false) trackShotEdit('frame_relink', undefined, role)
    return true
  }
  const updateExactShotCount = (count: number | undefined) => {
    if (!canvas?.transact || !node || !isTextShotList || shotRows.length || status === 'running') {
      throw new Error('Cannot change Shot List count during generation or without a text canvas')
    }
    if (count !== undefined && (!Number.isSafeInteger(count) || count < 1)) {
      throw new Error('Exact shot count must be a positive integer')
    }
    canvas.transact(() => {
      if (count === undefined) node.delete('in_shot_count')
      else node.writeInShotCount(count)
    })
  }
  const assertShotEditable = () => {
    if (!canvas?.transact || !node || !isShotList) throw new Error('Shot List canvas is unavailable')
    if (['running', 'pending_execution', 'pending_cost_confirmation'].includes(dataStatus(getNodes().find(other => other.id === id)?.data))) {
      throw new ShotEditError('shotList.errors.waitForShotList', 'Wait for the Shot List to finish before editing')
    }
    const childIds = new Set([
      ...frameLinks.map(link => link.node_id),
      ...videoPacks.map(pack => pack.video_node_id),
    ])
    if (getNodes().some(child => childIds.has(child.id) &&
      ['running', 'pending_execution', 'pending_cost_confirmation'].includes(dataStatus(child.data)))) {
      throw new ShotEditError('shotList.errors.waitForJobs', 'Wait for connected frame and video jobs to finish before editing')
    }
  }
  const invalidateShotOutputs = () => {
    if (!node) throw new Error('Shot List canvas is unavailable')
    node.delete('out_frame_links')
    node.delete('out_start_frames')
    node.delete('out_key_frames')
    node.delete('out_end_frames')
    node.delete('out_start_frame_prompts')
    node.delete('out_key_frame_prompts')
    node.delete('out_end_frame_prompts')
    node.delete('out_production_plan')
    node.delete('out_video_generation_plan')
  }
  const updateShotCell = (rowId: string, field: ShotEditField, draft: string, expected?: string) => {
    assertShotEditable()
    if (!node || !canvas?.transact) throw new Error('Shot List canvas is unavailable')
    const rows = shotEditSource === 'flat' ? node.get('shot_list') ?? [] : node.get('out_shots') ?? []
    const seams = node.get('out_shot_seams') ?? []
    const index = rows.findIndex(row => row.id === rowId)
    const row = rows[index]
    if (!row || rows.some((other, idx) => idx !== index && other.id === rowId)) throw new ShotEditError('shotList.errors.shotMissing', `Shot ${rowId} is missing or duplicated`, { id: rowId })
    if (shotEditSource !== 'flat' && seams.length !== rows.length + 1) throw new ShotEditError('shotList.errors.seamsChanged', 'Shot List seams changed; refresh and try again')
    if ((field === 'boundary_type' && draft === 'Continuous' || field === 'transition' && draft.trim().toLowerCase() === 'continuous') &&
      (index === rows.length - 1 ||
      (rows[index + 1]?.scene_ordinal !== undefined && rows[index + 1].scene_ordinal !== row.scene_ordinal))) {
      throw new ShotEditError('shotList.errors.continuousScene', 'Continuous requires a following shot in the same scene')
    }
    const after = validateShotEdit(shotEditSource, field, draft, shotListVideoEnabled)
    const before = readShotField(row, seams, index, field)
    if (expected !== undefined && before !== expected) {
      textUiLog({ event: 'shot-list:edit-conflict', feature: 'shot-list', node_id: id, row_id: rowId, field })
      throw new ShotEditError('shotList.errors.cellConflict', 'This cell changed elsewhere while you were editing; review it before saving')
    }
    if (before === after) return true
    const beforeVisualPrompt = field === 'visual_description' && 'generation_id' in row ? row.visual_prompt : undefined
    const beforeNeighborTransition = shotEditSource === 'flat' && field === 'transition' ? node.get('shot_list')?.[index + 1]?.transition_in : undefined
    const beforeBoundary = shotEditSource !== 'flat' && (field === 'transition' || field === 'opening_transition')
      ? seams[field === 'opening_transition' ? 0 : index + 1]?.boundary_type : undefined
    const needsPaidFrames = shotEditSource !== 'flat' && frameLinks.some(link => link.status === 'ready')
    if (needsPaidFrames && !window.confirm(t('shotList.confirmEditFrames'))) return false
    canvas.transact(() => {
      assertShotEditable()
      const liveRows = shotEditSource === 'flat' ? node.get('shot_list') ?? [] : node.get('out_shots') ?? []
      const liveSeams = node.get('out_shot_seams') ?? []
      const live = liveRows[index]
      if (live?.id !== rowId || liveRows.length !== rows.length || readShotField(live, liveSeams, index, field) !== before) {
        throw new ShotEditError('shotList.errors.shotChanged', `Shot ${rowId} changed while you were editing; refresh the cell`, { id: rowId })
      }
      if (field === 'visual_description' && 'generation_id' in live && live.visual_prompt !== beforeVisualPrompt) {
        throw new ShotEditError('shotList.errors.promptChanged', `Shot ${rowId} prompt changed while you were editing; refresh the cell`, { id: rowId })
      }
      if (field === 'transition' && shotEditSource === 'flat' &&
        node.get('shot_list')?.[index + 1]?.transition_in !== beforeNeighborTransition) {
        throw new ShotEditError('shotList.errors.transitionChanged', `Shot ${rowId} next transition changed while you were editing`, { id: rowId })
      }
      if (field !== 'boundary_type' && shotEditSource !== 'flat' &&
        (field === 'transition' || field === 'opening_transition') &&
        liveSeams[field === 'opening_transition' ? 0 : index + 1]?.boundary_type !== beforeBoundary) {
        throw new ShotEditError('shotList.errors.boundaryChanged', `Shot ${rowId} boundary changed while you were editing`, { id: rowId })
      }
      writeShotField(live, liveSeams, index, field, after)
      const nextFlat = node.get('shot_list')?.[index + 1]
      if (!('generation_id' in live) && field === 'transition' && nextFlat) {
        nextFlat.setTransitionIn(after)
      }
      if (shotEditSource !== 'flat') {
        invalidateShotOutputs()
        if (field === 'dialogue_voiceover') node.delete('shot_guidance_warning')
      }
    })
    const afterBoundary = shotEditSource !== 'flat' && (field === 'transition' || field === 'opening_transition')
      ? node.get('out_shot_seams')?.[field === 'opening_transition' ? 0 : index + 1]?.boundary_type : undefined
    shotEditHistory.current.undo.push({ source: shotEditSource, rowId, field, before, after, beforeVisualPrompt, beforeNeighborTransition, beforeBoundary, afterBoundary })
    if (shotEditHistory.current.undo.length > 50) shotEditHistory.current.undo.shift()
    shotEditHistory.current.redo = []
    textUiLog({ event: 'shot-list:cell-updated', feature: 'shot-list', node_id: id, row_id: rowId, field })
    trackShotEdit('cell_commit', field)
    return true
  }
  const updateOpeningTransition = (draft: string, _boundary: 'Hard' | 'Soft', expected?: string) => {
    if (!shotRows[0] || !isTextShotList) throw new Error('Opening transition needs a generated shot')
    return updateShotCell(shotRows[0].id, 'opening_transition', draft, expected)
  }
  const updateShotFraming = (rowId: string, field: FramingField, value: string) => {
    updateShotCell(rowId, `framing_${field}`, value)
  }
  const onShotListKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    // SHORTCUT_ID: shotList.undo
    // SHORTCUT_ID: shotList.redo
    if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'z' ||
      event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return
    if (!canvas?.transact || !node) throw new Error('Cannot undo Shot List edit without a canvas transaction')
    const from = event.shiftKey ? shotEditHistory.current.redo : shotEditHistory.current.undo
    const to = event.shiftKey ? shotEditHistory.current.undo : shotEditHistory.current.redo
    const edit = from[from.length - 1]
    if (!edit) return
    if ('role' in edit) {
      const expected = event.shiftKey ? edit.before : edit.after ? { imageNodeId: edit.after, origin: 'user' } : undefined
      const replacement = event.shiftKey ? edit.after : edit.before?.imageNodeId
      const liveAnchor = edit.role !== 'key' ? node.get('in_video_anchors')?.find(item => item.shot_id === edit.shotId &&
        item.role === (edit.role === 'start' ? 'first' : 'last') && item.exact !== false) : undefined
      const liveKey = edit.role === 'key' ? node.get('shot_key_frame_relinks')?.find(item => item.shot_id === edit.shotId) : undefined
      const actual = liveKey?.image ? { imageNodeId: liveKey.image, origin: 'user' }
        : liveAnchor?.image ? { imageNodeId: liveAnchor.image, origin: liveAnchor.origin === 'user_relink' ? 'user' : 'director' } : undefined
      if ((actual?.imageNodeId ?? undefined) !== expected?.imageNodeId || expected?.origin !== actual?.origin) {
        from.pop()
        textUiLog({ event: 'shot-list:undo-conflict', feature: 'shot-list', node_id: id, shot_id: edit.shotId, role: edit.role })
      } else {
        try {
          changeFrameRelink(edit.shotId, edit.role, replacement ?? '', {
            recordHistory: false, restoreDirector: !event.shiftKey && edit.before?.origin === 'director',
          })
          from.pop()
          to.push(edit)
          trackShotEdit(event.shiftKey ? 'redo' : 'undo', undefined, edit.role)
        } catch (error) {
          textUiLog({ event: 'shot-list:undo-invalid', feature: 'shot-list', node_id: id, shot_id: edit.shotId, role: edit.role, error: String(error) })
        }
      }
      event.preventDefault()
      event.stopPropagation()
      return
    }
    const expected = event.shiftKey ? edit.before : edit.after
    const replacement = event.shiftKey ? edit.after : edit.before
    const rows = edit.source === 'flat' ? node.get('shot_list') ?? [] : node.get('out_shots') ?? []
    const seams = node.get('out_shot_seams') ?? []
    const index = rows.findIndex(item => item.id === edit.rowId)
    const row = rows[index]
    if (shotEditSource !== edit.source || !row || readShotField(row, seams, index, edit.field) !== expected) {
      from.pop()
      textUiLog({ event: 'shot-list:undo-conflict', feature: 'shot-list', node_id: id, row_id: edit.rowId, field: edit.field })
      event.preventDefault()
      event.stopPropagation()
      return
    }
    try {
      assertShotEditable()
      validateShotEdit(edit.source, edit.field, edit.field === 'duration_ms' ? durationSecondsFromMs(Number(replacement)) : replacement, shotListVideoEnabled)
    } catch (error) {
      event.preventDefault()
      event.stopPropagation()
      textUiLog({ event: 'shot-list:undo-invalid', feature: 'shot-list', node_id: id, row_id: edit.rowId, field: edit.field, error: String(error) })
      return
    }
    if (edit.source !== 'flat' && (node.get('out_frame_links') ?? []).some(link => link.status === 'ready') &&
      !window.confirm(t('shotList.confirmUndoFrames'))) {
      event.preventDefault()
      event.stopPropagation()
      return
    }
    try { canvas.transact(() => {
      assertShotEditable()
      const liveRows = edit.source === 'flat' ? node.get('shot_list') ?? [] : node.get('out_shots') ?? []
      const liveSeams = node.get('out_shot_seams') ?? []
      const live = liveRows[index]
      if (liveRows.length !== rows.length || !live || live.id !== edit.rowId ||
        readShotField(live, liveSeams, index, edit.field) !== expected ||
        (edit.source !== 'flat' && (edit.field === 'transition' || edit.field === 'opening_transition') &&
          liveSeams[edit.field === 'opening_transition' ? 0 : index + 1]?.boundary_type !==
            (event.shiftKey ? edit.beforeBoundary : edit.afterBoundary)) ||
        (edit.field === 'transition' && edit.source === 'flat' && node.get('shot_list')?.[index + 1] &&
          node.get('shot_list')?.[index + 1]?.transition_in !== (event.shiftKey ? edit.beforeNeighborTransition : edit.after)) ||
        (edit.field === 'visual_description' && 'generation_id' in live &&
          live.visual_prompt !== (event.shiftKey ? edit.beforeVisualPrompt : undefined))) {
        throw new Error('Shot changed before undo could be applied')
      }
      writeShotField(live, liveSeams, index, edit.field, replacement)
      if (edit.source !== 'flat' && (edit.field === 'transition' || edit.field === 'opening_transition')) {
        const seam = liveSeams[edit.field === 'opening_transition' ? 0 : index + 1]
        if (!seam) throw new Error('Shot transition seam disappeared during undo')
        const boundary = event.shiftKey ? edit.afterBoundary : edit.beforeBoundary
        if (boundary === undefined) seam.deleteBoundaryType()
        else seam.setBoundaryType(boundary)
      }
      if (edit.field === 'visual_description' && !event.shiftKey && edit.beforeVisualPrompt !== undefined && 'generation_id' in live) {
        live.setVisualPrompt(edit.beforeVisualPrompt)
      }
      if (edit.source === 'flat' && edit.field === 'transition') {
        const next = node.get('shot_list')?.[index + 1]
        if (next) {
          if (!event.shiftKey && edit.beforeNeighborTransition === undefined) next.deleteTransitionIn()
          else next.setTransitionIn(event.shiftKey ? edit.after : edit.beforeNeighborTransition ?? '')
        }
      }
      if (edit.source !== 'flat') {
        invalidateShotOutputs()
        if (edit.field === 'dialogue_voiceover') node.delete('shot_guidance_warning')
      }
    }) } catch (error) {
      event.preventDefault()
      event.stopPropagation()
      textUiLog({ event: 'shot-list:undo-conflict', feature: 'shot-list', node_id: id, row_id: edit.rowId, field: edit.field, error: String(error) })
      return
    }
    from.pop()
    to.push(edit)
    event.preventDefault()
    event.stopPropagation()
    textUiLog({ event: event.shiftKey ? 'shot-list:redo' : 'shot-list:undo', feature: 'shot-list', node_id: id, row_id: edit.rowId, field: edit.field })
    trackShotEdit(event.shiftKey ? 'redo' : 'undo', edit.field)
  }
  const selectSingleNode = useSelectSingleCanvasNode()
  const framePreview = (link: ShotFrameLinkDict): string | undefined => {
    return imagePreview(link.node_id)
  }
  const needsFrameHandoff = frameLinks.some(link => {
    if (link.status !== 'ready') return false
    const child = getNodes().find(other => other.id === link.node_id)
    return !framePreview(link) || (child != null && dataStatus(child.data) === 'idle')
  })
  const focusFrameNode = (link: ShotFrameTarget) => {
    if (!imagePreview(link.node_id)) throw new Error(`Shot frame ${link.node_id} is not available on the canvas`)
    const child = getNodes().find(other => other.id === link.node_id)
    const parentId = child?.parentId ?? child?.parentNode
    const group = parentId ? getNodes().find(other => other.id === parentId) : undefined
    const rawGroup = group?.type === 'nodeGroup' ? canvas?.getYNode?.(group.id) : undefined
    const typedGroup = rawGroup ? bindCanvasNodeByFeType(rawGroup, 'nodeGroup') : undefined
    const expandGroup = typedGroup?.get('in_collapsible') === true && typedGroup.get('in_collapsed') === true
    if (expandGroup && group) {
      if (!canvas) throw new Error(`Cannot expand Shot List frame group ${group.id} without a canvas`)
      canvas.updateNodeData({ id: group.id, type: 'nodeGroup' }, { in_collapsed: false })
      setNodes(nodes => nodes.map(other => other.id === group.id
        ? { ...other, data: { ...other.data, in_collapsed: false } } : other))
    }
    const focus = async () => {
      selectSingleNode(link.node_id)
      // A child hidden since page load has no ReactFlow dimensions until its
      // expanded group renders. fitView returns false until it is measurable.
      for (let attempt = 0; attempt < 60; attempt++) {
        if (await fitView({ nodes: [{ id: link.node_id }], duration: 250, padding: 0.35, maxZoom: 1.15 })) {
          textUiLog({ event: 'shot-list:frame-focused', feature: 'shot-list', node_id: id, frame_node_id: link.node_id, shot_id: link.shot_id, role: link.role, group_expanded: expandGroup })
          return
        }
        await new Promise<void>(resolve => requestAnimationFrame(() => resolve()))
      }
      throw new Error(`Shot frame ${link.node_id} was not measurable after expanding its group`)
    }
    void focus().catch(error => {
      textUiLog({ event: 'shot-list:frame-focus-failed', feature: 'shot-list', node_id: id, frame_node_id: link.node_id, error: String(error) })
      console.error(error)
    })
  }

  const in_script = node?.get('in_script') ?? ''
  const in_name = node?.get('in_name')
  // "Storyboard Designer 1" is a Director-assigned default stored in English
  // (the backend re-parses it for ordinals and @-mention lookup), so it is
  // localized on the way out only. Returns undefined once the user renames.
  const displayName =
    localizeDefaultNodeName(
      'storyboardDesigner',
      { in_name, name_autogen: node?.get('name_autogen') },
      t,
    ) ?? in_name
  const in_style = node?.get('in_style')
  // Mirrors the schema default in schemas/nodes/storyboard_designer.json. Must
  // stay an ungated model: the GPT Image 2.5 variants are behind the
  // `gpt_image_2_5` runtime feature gate and would not render as a chip here.
  const in_model = node?.get('in_model') ?? 'openai-gpt-image-2'
  const in_panel_count = node?.get('in_panel_count') ?? 9
  const out_beats: string[] | undefined = node?.get('out_beats')
  const { handleDelete, handleDuplicate, handleAddToChat } = useNodeActions({ nodeId: id })
  const duplicateMenuItems = useDuplicateMenuItems(handleDuplicate, displayName || t('storyboardDesignerNode.storyboard'))
  const { saveState, handleSaveToLibrary, saveLabel } = useSaveToLibrary({ nodeId: id, selected })
  // `in_style` is a stable slug persisted in YDoc and read by the backend, so
  // only the DISPLAY label is localized — same resolution chain the Config tab
  // already uses (user styles -> locale-aware /styles -> built-in styleLabels).
  const { label: styleLabel } = useStyleLabel(in_style)

  const [isHoveringLightweightNodeChrome, setIsHoveringLightweightNodeChrome] = useState(false)

  const isRunning = status === 'running'
  const isPendingExecution = status === 'pending_execution'
  const isPendingCost = status === 'pending_cost_confirmation'
  const showSpinner = isRunning || isPendingExecution || isPendingCost
  const shotListNuxReady = isShotList && shotRows.length > 0 && !showSpinner
  const shotListEmptyState: ShotListEmptyState = isTextShotList && !shotRows.length
    ? isRunning ? 'running' : isPendingExecution ? 'queued' : isPendingCost ? 'awaiting_cost' : 'empty'
    : 'empty'
  const shouldRenderNodeChrome =
    !lightweightNodeChrome || !!selected || isHoveringLightweightNodeChrome
  const spinClass = isRunning ? 'animate-spin' : isPendingCost ? '' : 'animate-spin-slow'
  const isError = status === 'error'
  // Compute the sanitized, localized error once so the tooltip (`title`) never
  // exposes oversized/structured provider prose that sanitizeNodeError strips
  // from the rendered text (CodeRabbit: use the sanitized message for both).
  const displayError = isError && error ? sanitizeNodeError(resolveNodeErrorMessage(error, t), t, true) : undefined
  const batchPending = (status === 'running') ? (node?.get('batch_pending') ?? 0) : 0
  const [isCancelling, setIsCancelling] = useState(false)

  // ── Grid sections from beats ──────────────────────────────────────────

  const gridSections = useMemo<GridSection[]>(() => {
    void cacheKey
    const beats = Array.isArray(out_beats) ? out_beats.map(String) : []
    return buildGridSections(in_panel_count, beats)
  }, [in_panel_count, out_beats, cacheKey])

  const hasBeats = Array.isArray(out_beats) && out_beats.length > 0
  const gridCount = gridSections.length

  // NAD trace: log grid decomposition for debugging
  textUiLog({
    event: 'storyboard-text-ui:render',
    trace_id: `storyboard-text-ui:${id}`,
    node_id: id,
    panel_count: in_panel_count,
    beat_count: out_beats?.length ?? 0,
    grid_count: gridCount,
    has_beats: hasBeats,
    grids: gridSections.map(s => `${s.size[0]}x${s.size[1]}`),
  })

  // ── Subtitle ──────────────────────────────────────────────────────────

  const subtitleParts = useMemo(() => {
    const parts: string[] = []
    parts.push(t('storyboardDesignerNode.subtitlePanels', { count: in_panel_count }))
    parts.push(t('storyboardDesignerNode.subtitleGrids', { count: gridCount }))
    // Guarded on the raw slug, not on styleLabel: useStyleLabel returns an
    // untranslated "Style" fallback for an empty id, which must not leak here.
    if (in_style) parts.push(styleLabel)
    parts.push(getImageModelLabel(in_model))
    return parts.join(' · ')
  }, [in_panel_count, gridCount, in_style, styleLabel, in_model, t])

  // ── Scroll trapping ───────────────────────────────────────────────────

  const stopWheelPropagation = useCallback((e: ReactWheelEvent<HTMLDivElement>) => {
    e.stopPropagation()
  }, [])

  // ── Cancel handler ────────────────────────────────────────────────────

  const handleCancel = useCallback(() => {
    if (!isRunning || !projectId || isCancelling) return
    setIsCancelling(true)
    trackGenerationCancelled({ projectId, nodeId: id, nodeType: 'storyboard_designer', stage: 'running' })
    cancelNodeExecution(projectId, id, canvasId)
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('cancelNodeExecution failed:', err)
      })
      .finally(() => setIsCancelling(false))
  }, [isRunning, projectId, id, isCancelling, canvasId])

  debug('render node=%s status=%s beats=%d grids=%d', id, status, out_beats?.length ?? 0, gridCount)

  // ── Bottom panel state ────────────────────────────────────────────────

  const [bottomPanelOpen, setBottomPanelOpen] = useState(false)
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB)
  const tabs = useMemo<TabDefinition[]>(
    () => CONFIG_TABS_STATIC.map((tab) => ({ ...tab, label: t(`storyboardDesignerNode.${tab.id}`) })),
    [t],
  )

  useBottomPanelAutoOpen({
    selected,
    isRunning,
    batchPending,
    setBottomPanelOpen,
    setActiveTab,
  })

  const openPanelToTab = useCallback(
    (tab: string) => {
      setActiveTab(tab)
      setBottomPanelOpen(true)
      selectSingleNode(id)
    },
    [id, selectSingleNode]
  )

  // ── Config updates ────────────────────────────────────────────────────

  const updateConfig = useCallback(
    <K extends StoryboardDesignerConfigUpdateKey,>(
      key: K,
      value: StoryboardDesignerNodeData[K],
    ) => {
      setNodes((nds) =>
        nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, [key]: value } } : n))
      )
      canvas?.updateNodeData({ id, type: 'storyboardDesigner' }, { [key]: value })
    },
    [id, setNodes, canvas]
  )

  // ── Generate handler ──────────────────────────────────────────────────

  const showInsufficientSparks = useShowInsufficientSparks()
  const { isLocked, run } = useClickLock(1000)
  const generateLocked = isLocked(id)

  const handleGenerate = useCallback(async (retryCount: number = 1, explicitAction?: string) => {
    if (status === 'running' || status === 'pending_execution' || status === 'pending_cost_confirmation') return
    if (!projectId) {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? { ...n, data: { ...n.data, status: 'error', error: encodeNodeError({ code: 'node.missingProject' }) } }
            : n
        )
      )
      return
    }

    void run(id, async () => {
      if (!explicitAction) {
        setNodes((nds) =>
          nds.map((n) =>
            n.id === id
              ? { ...n, data: { ...n.data, status: 'running', progress: 0, error: undefined } }
              : n
          )
        )
      }

      try {
        await executeNode(projectId, id, {
          count: retryCount, canvasId,
          ...(explicitAction ? { action: explicitAction } : isTextShotList ? { action: 'generate_shot_list' } : {}),
        })
      } catch (err) {
        if (err instanceof InsufficientSparksError) {
          showInsufficientSparks({
            balance: err.balance,
            required: err.required,
            nodeId: id,
            nodeType: 'storyboard_designer',
            projectId,
          })
          setNodes((nds) =>
            nds.map((n) =>
              n.id === id
                ? { ...n, data: { ...n.data, status: 'idle', error: undefined } }
                : n
            )
          )
          return
        }
        setNodes((nds) =>
          nds.map((n) =>
            n.id === id
              ? { ...n, data: { ...n.data, status: 'error', error: String(err) } }
              : n
          )
        )
      }
    })
  }, [projectId, id, status, setNodes, run, showInsufficientSparks, canvasId, isTextShotList])

  // ── Reference handling for ConfigTabContent ───────────────────────────

  const currentRefs = useMemo<SelectedReference[]>(() => {
    const nodes = getNodes()
    return [
      ...buildAssetReferencesFromEdges(nodes, edges, id, 'in_characters', 'image'),
      ...buildAssetReferencesFromEdges(nodes, edges, id, 'in_locations', 'image'),
      ...buildAssetReferencesFromEdges(nodes, edges, id, 'in_objects', 'image'),
      ...buildAssetReferencesFromEdges(nodes, edges, id, 'in_image', 'image').slice(0, 1),
    ]
  }, [getNodes, edges, id])

  const handleReferencesChange = useCallback(
    (refs: SelectedReference[]) => {
      if (canvas) {
        const characterRefs = refs.filter((ref) => ref.nodeType === 'characterDesigner')
        const locationRefs = refs.filter((ref) => ref.nodeType === 'locationDesigner')
        const objectRefs = refs.filter((ref) => ref.nodeType === 'objectDesigner')
        const genericRefs = refs.filter(
          (ref) => !['characterDesigner', 'locationDesigner', 'objectDesigner'].includes(ref.nodeType)
        )
        const nameEdges = (items: SelectedReference[]) =>
          items.map((ref, index) => ({
            source: ref.nodeId,
            sourceHandle: 'out_name',
            toIndex: index,
          }))
        const imageEdges = (items: SelectedReference[]) =>
          items.map((ref, index) => ({
            source: ref.nodeId,
            sourceHandle: getImageOutputPort(ref.nodeType),
            toIndex: index,
          }))

        const applyEdges = () => {
          canvas.replacePortEdges(id, 'in_characters', imageEdges(characterRefs))
          canvas.replacePortEdges(id, 'in_character_names', nameEdges(characterRefs))
          canvas.replacePortEdges(id, 'in_locations', imageEdges(locationRefs))
          canvas.replacePortEdges(id, 'in_location_names', nameEdges(locationRefs))
          canvas.replacePortEdges(id, 'in_objects', imageEdges(objectRefs))
          canvas.replacePortEdges(id, 'in_object_names', nameEdges(objectRefs))
          canvas.replacePortEdges(id, 'in_image', buildImageReferenceEdgeUpdates(genericRefs.slice(0, 1)))
        }

        if (canvas.transact) {
          canvas.transact(applyEdges)
        } else {
          applyEdges()
        }
      }
    },
    [canvas, id]
  )

  // ── Toolbar actions ───────────────────────────────────────────────────

  const toolbarActions: ToolbarAction[] = useMemo(
    () => [
      {
        key: 'save-to-library',
        label: saveLabel,
        icon:
          saveState === 'loading' ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : saveState === 'success' ? (
            <Check className="w-4 h-4 text-green-400" />
          ) : (
            <BookmarkPlus className="w-4 h-4" />
          ),
        onClick: handleSaveToLibrary,
        disabled: saveState !== 'idle',
        group: true,
      },
      {
        key: 'addToChat',
        label: t('storyboardDesignerNode.addToChat'),
        icon: <AddToChatIcon size={14} />,
        onClick: handleAddToChat,
      },
      {
        key: 'duplicate',
        label: t('storyboardDesignerNode.duplicate'),
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        ),
        menuItems: duplicateMenuItems,
        group: true,
      },
      {
        key: 'delete',
        label: t('storyboardDesignerNode.delete'),
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        ),
        onClick: handleDelete,
        variant: 'danger',
      },
    ],
    [duplicateMenuItems, handleDelete, handleSaveToLibrary, saveState, saveLabel, handleAddToChat, t]
  )

  // ── Panel visibility ──────────────────────────────────────────────────

  const panelVisible = !!selected && !dragging && bottomPanelOpen

  // ── First beat preview for FlatIdleNodeCard ───────────────────────────

  const firstBeatPreview = useMemo(() => {
    if (!hasBeats || !out_beats) return undefined
    const first = out_beats[0]
    return typeof first === 'string' ? first : undefined
  }, [hasBeats, out_beats])

  // ── Render ──────────────────────────────────────────────────────────────

  if (isShotList) {
    return (
      <div
        className="relative"
        data-testid="shot-list-node"
        data-nux-anchor={shotListNuxReady ? 'shot-list-overview' : undefined}
        data-nux-node-id={id}
        data-nux-node-type="storyboardDesigner"
        data-nux-node-status={shotListNuxReady ? 'completed' : status}
        onClickCapture={() => emitNuxEvent({
          type: 'controlActivated',
          controlId: 'shotListInteracted',
          anchorId: 'shot-list-overview',
          nodeId: id,
          nodeType: 'storyboardDesigner',
        })}
        onKeyDown={onShotListKeyDown}
      >
        <AgentNodeToolbar actions={toolbarActions.filter(action => action.key === 'duplicate' || action.key === 'delete')} isVisible={!!selected && !dragging} />
        <FloatingNodeLabel name={displayName || ''} fallback={t('shotList.title', { defaultValue: 'Shot List' })} />
        <div className={`canvas-node-card-contained ${NODE_CARD_RADIUS_CLASS} flex flex-col overflow-hidden bg-surface-1`}
          style={{ width: nodePresentationWidth(SHOT_LIST_NODE_DIMS.width), height: SHOT_LIST_NODE_DIMS.height }}>
          {handlesVisible && HANDLES.map(handle => <Handle key={handle.id} type={handle.type} position={handle.position}
            id={handle.id} className="!bg-transparent !border-0 !w-2 !h-2" style={handle.style} />)}
          <div className="flex items-center justify-between border-b border-border bg-surface-0 px-3 py-2 text-sm text-foreground">
            <span>{t('shotList.title', { defaultValue: 'Shot List' })}</span>
            <div className="flex items-center gap-3">
              {isTextShotList && shotRows.length === 0 && <label className="flex items-center gap-1 text-xs text-muted-foreground">
                {t('shotList.countMode', { defaultValue: 'Count' })}
                <select value={exactShotCount === undefined ? 'auto' : 'exact'}
                  disabled={showSpinner}
                  aria-label={t('shotList.countMode', { defaultValue: 'Shot count mode' })}
                  onChange={event => { setShotCountDraft(null); updateExactShotCount(event.currentTarget.value === 'auto' ? undefined : 1) }}
                  className="nodrag h-7 rounded-lg border border-input-border bg-input-bg px-1 text-xs text-foreground hover:bg-surface-3 hover:border-input-border-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20">
                  <option value="auto">{t('shotList.autoCount')}</option><option value="exact">{t('shotList.exactCountMode')}</option>
                </select>
                {exactShotCount !== undefined && <input type="number" min={1} step={1}
                  value={shotCountDraft ?? exactShotCount} disabled={showSpinner}
                  aria-label={t('shotList.exactCount', { defaultValue: 'Exact shot count' })}
                  onChange={event => setShotCountDraft(event.currentTarget.value)}
                  onBlur={() => {
                    if (shotCountDraft !== null) {
                      const count = Number(shotCountDraft)
                      if (shotCountDraft && Number.isSafeInteger(count) && count > 0) updateExactShotCount(count)
                      setShotCountDraft(null)
                    }
                  }}
                  onKeyDown={event => { if (event.nativeEvent.isComposing || event.keyCode === 229) return; if (event.key === 'Enter') event.currentTarget.blur(); if (event.key === 'Escape') setShotCountDraft(null) }}
                  className="nodrag h-7 w-14 rounded-lg border border-input-border bg-input-bg px-1 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20" />}
              </label>}
              <span className="text-muted-foreground">{t('shotList.shotCount', { count: shotRows.length, defaultValue: `${shotRows.length} shots` })}</span>
              {effectiveShotListView === 'shot-list' && <button type="button" onClick={() => {
                setShotListExpanded(true)
                textUiLog({ event: 'shot-list:expanded', feature: 'shot-list', node_id: id })
              }} aria-label={t('shotList.expand')} title={t('shotList.expand')}
                className="nodrag inline-flex h-7 items-center gap-1 rounded-lg border border-input-border bg-surface-1 px-2 text-xs text-foreground hover:border-input-border-hover hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20">
                <Expand className="h-4 w-4" />{t('shotList.expand')}
              </button>}
              {isTextShotList && hasStoryboardContent && <div role="group" aria-label={t('shotList.viewLabel', { defaultValue: 'Shot List view' })} className="nodrag flex rounded-lg border border-input-border bg-input-bg p-0.5">
                {SHOT_LIST_VIEWS.map(view => <button type="button" key={view}
                  aria-pressed={effectiveShotListView === view} onClick={() => setShotListView(view)}
                  className={`rounded-lg px-2 py-1 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 ${effectiveShotListView === view ? 'bg-surface-3 text-foreground' : 'text-muted-foreground hover:bg-surface-3 hover:text-foreground'}`}>
                  {t(`shotList.views.${view}`, { defaultValue: view === 'shot-list' ? 'Shot List' : 'Storyboard' })}
                </button>)}
              </div>}
              {isTextShotList && <button type="button" onClick={() => void handleGenerate()}
                disabled={showSpinner || shotRows.length > 0 || !projectId}
                className="nodrag h-7 rounded-lg border border-input-border bg-surface-1 px-2 text-xs text-foreground hover:bg-surface-3 hover:border-input-border-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:opacity-50">
                {isPendingCost ? t('storyboardDesignerNode.waitingForCostApproval') : isPendingExecution
                  ? t('shotList.queued') : isRunning
                    ? t('shotList.generating', { defaultValue: 'Generating…' })
                    : t('shotList.generate', { defaultValue: 'Generate Shot List' })}
              </button>}
              {isTextShotList && shotRows.length > 0 && <div className="nodrag flex items-center gap-1">
                {productionPlanningEnabled && node?.get('shot_list_planner_version') !== 2 && <button type="button"
                  onClick={() => void handleGenerate(1, 'generate_production_plan')}
                  disabled={!projectId || generateLocked || status === 'running' || status === 'pending_execution' || status === 'pending_cost_confirmation' || shotRows.length > 24}
                  title={shotRows.length > 24 ? t('shotList.productionTooMany') : t('shotList.productionGenerate')}
                  className="h-7 rounded-lg border border-input-border bg-surface-1 px-2 text-xs text-foreground hover:bg-surface-3 hover:border-input-border-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:opacity-50">
                  {t('shotList.productionGenerate')}
                </button>}
                <label htmlFor={`frame-mode-${id}`} className="sr-only">{t('shotList.frameMode', { defaultValue: 'Storyboard frame mode' })}</label>
                <select id={`frame-mode-${id}`} value={frameMode} onChange={event => {
                  const selected = event.currentTarget.value
                  if (selected !== 'key' && selected !== 'start_end' && selected !== 'all') throw new Error(`Unsupported Shot List frame mode: ${selected}`)
                  setFrameMode(selected)
                }}
                  disabled={status === 'running' || status === 'pending_execution' || status === 'pending_cost_confirmation'}
                  className="nodrag h-7 rounded-lg border border-input-border bg-input-bg px-1 text-xs text-foreground hover:border-input-border-hover hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20">
                  <option value="key">{t('shotList.modes.key', { defaultValue: 'Key frame' })}</option>
                  <option value="start_end">{t('shotList.modes.start_end', { defaultValue: 'Start + end' })}</option>
                  <option value="all">{t('shotList.modes.all', { defaultValue: 'All three' })}</option>
                </select>
                <button type="button" onClick={() => {
                  if (Object.values(selectedFrames).some(frame => frame.origin === 'user') &&
                    !window.confirm(t('shotList.confirmGenerateFrames'))) return
                  void handleGenerate(1, {
                    key: 'generate_shot_frames_key', start_end: 'generate_shot_frames_start_end', all: 'generate_shot_frames_all',
                  }[frameMode])
                }}
                  disabled={!projectId || generateLocked || status === 'running' || status === 'pending_execution' || status === 'pending_cost_confirmation' ||
                    !shotRows.some(shot => ({
                      key: ['key'], start_end: ['start', 'end'], all: ['start', 'key', 'end'],
                    }[frameMode]).some(role => !frameLinks.some(link => link.shot_id === shot.id && link.role === role && link.status === 'ready')))}
                  className="nodrag h-7 rounded-lg border border-input-border bg-surface-1 px-2 text-xs text-foreground hover:border-input-border-hover hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:opacity-50">
                  {t('shotList.generateStoryboard', { defaultValue: 'Generate Storyboard' })}
                </button>
                {needsFrameHandoff && <button type="button"
                  onClick={() => void handleGenerate(1, 'reconcile_shot_frames')}
                  disabled={!projectId || generateLocked || status === 'running' || status === 'pending_execution' || status === 'pending_cost_confirmation'}
                  className="nodrag h-7 rounded-lg border border-input-border bg-surface-1 px-2 text-xs text-foreground hover:border-input-border-hover hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:opacity-50">
                  {t('shotList.repairFrames')}
                </button>}
              </div>}
            </div>
          </div>
          {isTextShotList && displayError && <p role="alert" className="border-b border-border px-3 py-2 text-sm text-destructive">{displayError}</p>}
          {guidanceWarning && <p role="status" className="border-b border-border px-3 py-2 text-sm text-warning">{guidanceWarning}</p>}
          <div className="min-h-0 flex-1">
            {effectiveShotListView === 'storyboard' && isTextShotList
              ? <div className="nodrag nowheel h-full overflow-auto p-3" data-testid="shot-list-storyboard-view" onWheel={event => event.stopPropagation()}>
                {openingTransition(shotRows) && <p className="mb-3 text-xs text-muted-foreground">{t('shotList.sequenceStart')}: {openingTransition(shotRows)}</p>}
                <div className="grid grid-cols-3 gap-3">
                  {shotRows.map((shot, index) => <article key={shot.id} className="min-w-0 rounded-xl border border-border bg-surface-2 p-3 text-sm text-foreground">
                    <div className="mb-2 flex items-center justify-between gap-2 font-semibold">
                      <span>{t('shotList.shotLabel', { number: shot.shot_number || String(index + 1).padStart(2, '0'), defaultValue: `Shot ${index + 1}` })}</span>
                      <span className="text-muted-foreground">{shot.duration_ms / 1000}s</span>
                    </div>
                    <div className={`flex gap-2 ${portraitFrames ? 'flex-row' : 'flex-col'}`}>
                      <div className={`${portraitFrames ? 'w-2/5 shrink-0' : 'w-full'} space-y-1`}>
                        {FRAME_ROLES.filter(role => frameLinks.some(link => link.shot_id === shot.id && link.role === role) || selectedFrames[`${shot.id}:${role}`]).map(role => {
                          const link = frameLinks.find(item => item.shot_id === shot.id && item.role === role)
                          const chosen = selectedFrames[`${shot.id}:${role}`]
                          const preview = resolveShotFramePreview(chosen, link, framePreview)
                          const target = chosen ? { shot_id: shot.id, role, node_id: chosen.imageNodeId } : link
                          return <button type="button" key={role} disabled={!preview || !target} onClick={() => target && focusFrameNode(target)}
                            aria-label={t('shotList.openFrame', { number: shot.shot_number || String(index + 1).padStart(2, '0'), role: t(`shotList.columns.${role}_frame`), defaultValue: `Open shot ${index + 1} ${role} frame` })}
                            className="nodrag w-full rounded-lg border border-input-border bg-input-bg p-1 text-left text-xs hover:border-input-border-hover hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:opacity-60">
                            {preview && <img src={preview} alt="" className={`w-full rounded-lg object-contain ${portraitFrames ? 'h-28' : 'h-32'}`} />}
                            <span>{t(`shotList.columns.${role}_frame`)} · {chosen ? `${t(chosen.origin === 'director' ? 'shotList.directorAnchor' : 'shotList.relinked')} · ${t(preview ? 'shotList.ready' : 'shotList.unavailable')}`
                              : preview ? t('shotList.frameStatus.ready') : t(link?.status === 'ready' ? 'shotList.frameUnavailable' : `shotList.frameStatus.${link?.status}`)}</span>
                          </button>
                        })}
                      </div>
                      <div className="min-w-0 flex-1 space-y-1 text-muted-foreground">
                        <div className="font-semibold text-foreground">{shot.location || '—'}</div>
                        <ShotVisualDescription row={shot} />
                        {outgoingTransition(shot, shotRows[index + 1]).kind !== 'hard-cut' &&
                          <p>{t('shotList.columns.transition')}: <ShotOutgoingTransition row={shot} next={shotRows[index + 1]} /></p>}
                      </div>
                    </div>
                  </article>)}
                </div>
              </div>
              : <ShotListTable rows={shotRows} sourceKind={shotEditSource} onFramingChange={updateShotFraming}
                  onCellChange={updateShotCell} onOpeningTransitionChange={isTextShotList ? updateOpeningTransition : undefined}
                  openingSeamTransition={isTextShotList ? generatedSeams[0]?.transition : undefined}
                  onFrameRelink={isTextShotList ? changeFrameRelink : undefined} frameChoices={frameChoices}
                  duplicateFrameKeys={duplicateFrameKeys}
                  onFramePickerActiveChange={setFramePickerActive} selectedFrames={selectedFrames}
                  locationParts={Object.fromEntries(generatedShots.filter(shot => shot.id).map(shot => [shot.id!, {
                    interior_exterior: shot.interior_exterior ?? '', location: shot.location ?? '', time_of_day: shot.time_of_day ?? '',
                  }]))}
                  boundaryTypes={shotBoundaryTypes}
                  disabled={status === 'running' || !canvas || shotListExpanded}
                  emptyState={isTextShotList ? shotListEmptyState : undefined} announceEmptyState={!shotListExpanded}
                  videoPacks={videoPacks}
                  frameColumns={frameColumns} frameLinks={frameLinks} framePreview={framePreview} onFrameFocus={focusFrameNode} />}
          </div>
          {videoPlanStale && <p role="status" className="text-xs text-warning px-4 pb-2">{t('shotList.videoPlanStale', { defaultValue: 'Video generations are out of date. Replan before generating or stitching.' })}</p>}
        </div>
        <ExpandedShotListModal isOpen={shotListExpanded && effectiveShotListView === 'shot-list'} onClose={() => setShotListExpanded(false)}
          onUndoRedo={onShotListKeyDown}
          rows={shotRows} sourceKind={shotEditSource} emptyState={isTextShotList ? shotListEmptyState : undefined}
          onFramingChange={updateShotFraming}
          onCellChange={updateShotCell} onOpeningTransitionChange={isTextShotList ? updateOpeningTransition : undefined}
          openingSeamTransition={isTextShotList ? generatedSeams[0]?.transition : undefined}
          onFrameRelink={isTextShotList ? changeFrameRelink : undefined} frameChoices={frameChoices}
          duplicateFrameKeys={duplicateFrameKeys}
          onFramePickerActiveChange={setFramePickerActive} selectedFrames={selectedFrames}
          locationParts={Object.fromEntries(generatedShots.filter(shot => shot.id).map(shot => [shot.id!, {
            interior_exterior: shot.interior_exterior ?? '', location: shot.location ?? '', time_of_day: shot.time_of_day ?? '',
          }]))}
          boundaryTypes={shotBoundaryTypes}
          productionPlan={productionPlan} currentGenerationId={generatedShots[0]?.generation_id}
          videoPacks={videoPacks}
          currentScreenplayFingerprint={currentScreenplayFingerprint}
          disabled={status === 'running' || !canvas}
          frameColumns={frameColumns} frameLinks={frameLinks} framePreview={framePreview} onFrameFocus={focusFrameNode} />
      </div>
    )
  }

  if (lightweightNodeChrome && !selected && !dragging && !isHoveringLightweightNodeChrome) {
    return (
      <div
        data-testid="storyboard-designer-lightweight-host"
        className="w-fit"
        onPointerEnter={() => setIsHoveringLightweightNodeChrome(true)}
        onPointerLeave={() => setIsHoveringLightweightNodeChrome(false)}
      >
        <FlatIdleNodeCard
          nodeId={id}
          testId="storyboard-designer-flat-node"
          label={displayName}
          fallbackLabel={t('storyboardDesignerNode.storyboard')}
          width={NODE_WIDTH}
          status={status}
          error={displayError}
          progressMessage={progressMessage}
          handles={HANDLES}
          mediaKind="none"
          placeholderLabel={firstBeatPreview ? t('storyboardDesignerNode.panelPreview', { preview: firstBeatPreview.slice(0, 80) + '…' }) : t('storyboardDesignerNode.storyboard')}
          compactHandles
        />
      </div>
    )
  }

  return (
    <div
      className="relative"
      onPointerEnter={lightweightNodeChrome ? () => setIsHoveringLightweightNodeChrome(true) : undefined}
      onPointerLeave={lightweightNodeChrome ? () => setIsHoveringLightweightNodeChrome(false) : undefined}
    >
      <AgentNodeToolbar actions={toolbarActions} isVisible={!!selected && !dragging} />
      <FloatingNodeLabel name={displayName || ''} fallback={t('storyboardDesignerNode.storyboard')} />

      <div
        data-testid="storyboard-designer-node"
        className={`canvas-node-card-contained ${NODE_CARD_RADIUS_CLASS} overflow-hidden transition-all duration-150 flex flex-col ${showSpinner ? 'border-2 animate-border-glow' : ''}`}
        style={{
          width: nodePresentationWidth(NODE_WIDTH),
          height: 420,
          boxShadow: selected
            ? '0 0 0 2px rgba(255,255,255,0.7)'
            : isError
              ? '0 0 0 1px rgba(239,68,68,0.5)'
              : showSpinner
                ? '0 0 0 1px rgba(234,179,8,0.5)'
                : '0 2px 8px rgba(0,0,0,0.3)',
        }}
      >
        {/* Handles */}
        {handlesVisible && HANDLES.map((handle) => (
          <Handle
            key={handle.id}
            type={handle.type}
            position={handle.position}
            id={handle.id}
            className="!bg-transparent !border-0 !w-2 !h-2"
            style={handle.style}
          />
        ))}

        {/* Header */}
        {shouldRenderNodeChrome && (
          <div className="px-3 pt-2.5 pb-1.5 bg-surface-0 shrink-0">
            <div className="flex items-center gap-2">
              <span className="text-sm">🎬</span>
              <span className="text-xs font-medium text-foreground/90 truncate">
                {t('storyboardDesignerNode.storyboardDesigner')}
              </span>
              <span
                className="w-2 h-2 rounded-full shrink-0 ml-auto"
                style={{
                  backgroundColor: isError
                    ? 'var(--destructive)'
                    : showSpinner
                      ? 'var(--warning)'
                      : hasBeats
                        ? 'var(--primary)'
                        : 'var(--text-muted)',
                }}
              />
            </div>
            <p className="text-xxs text-foreground/40 mt-0.5 truncate">
              {subtitleParts}
            </p>
          </div>
        )}

        {/* Main content area */}
        <div className="relative flex-1 min-h-0 overflow-hidden rounded-b-lg">
          {showSpinner ? (
            <div className="w-full h-full bg-canvas flex flex-col items-center justify-center px-4">
              <div className={`w-6 h-6 border-2 border-[var(--warning)] border-t-transparent rounded-full ${spinClass}`} />
              <span className="text-xxs text-foreground/50 mt-2 text-center leading-relaxed">
                {isRunning
                  ? (progressMessage || t('storyboardDesignerNode.generatingStoryboard'))
                  : isPendingCost
                    ? t('storyboardDesignerNode.waitingForCostApproval')
                    : t('storyboardDesignerNode.queuedWaiting')}
              </span>
              {isRunning && (
                <span className="text-xxs text-foreground/25 mt-1">
                  {t('storyboardDesignerNode.mayTakeSeconds')}
                </span>
              )}
              {isRunning && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleCancel() }}
                  disabled={isCancelling}
                  className="mt-3 flex items-center gap-1.5 px-3 py-1 rounded-full border border-white/20 bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors nodrag"
                  data-testid="cancel-node-button"
                  title={t('storyboardDesignerNode.stop')}
                >
                  <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="currentColor">
                    <rect x="6" y="6" width="12" height="12" rx="1" />
                  </svg>
                  <span className="text-xxs font-medium">{t('storyboardDesignerNode.stop')}</span>
                </button>
              )}
            </div>
          ) : hasBeats ? (
            <div
              className="h-full overflow-y-auto px-3 py-2 space-y-2 nodrag nowheel bg-canvas"
              onWheel={stopWheelPropagation}
              data-testid="storyboard-beats-content"
            >
              {gridSections.map((section) => (
                <GridSectionView
                  key={section.gridIndex}
                  section={section}
                  defaultExpanded={gridSections.length <= 2}
                />
              ))}
            </div>
          ) : (
            <div
              className="w-full h-full bg-canvas flex flex-col items-center justify-center cursor-pointer"
              onClick={(e) => { e.stopPropagation(); openPanelToTab('config') }}
            >
              <svg className="w-8 h-8 text-foreground/10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
              </svg>
              <span className="text-xxs text-foreground/20 mt-1">{t('storyboardDesignerNode.storyboard')}</span>
            </div>
          )}

          {/* Status overlay — floats over the bottom of the content area
              rather than replacing it, so a failed re-run keeps the previous
              beats visible (post 1634117708345526). */}
          {isError && error && !showSpinner && (
            <NodeStatusMessage message={displayError} testId="storyboard-card-error" />
          )}
        </div>
      </div>

      {/* Unified Bottom Panel — Config only */}
      <UnifiedBottomPanel
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        isVisible={panelVisible}
      >
        {(tab) => {
          if (tab === 'config' && projectId) {
            return (
              <ConfigTabContent
                nodeId={id}
                nodeType="storyboardDesigner"
                prompt={in_script}
                onPromptChange={(value) => updateConfig('in_script', value)}
                references={currentRefs}
                onReferencesChange={handleReferencesChange}
                currentStyleId={in_style}
                onStyleChange={(styleId) => updateConfig('in_style', styleId)}
                model={in_model}
                onModelChange={(model) => updateConfig('in_model', model)}
                isRunning={isRunning || generateLocked}
                onGenerate={handleGenerate}
                onCancel={handleCancel}
                isCancelling={isCancelling}
              />
            )
          }
          return null
        }}
      </UnifiedBottomPanel>
    </div>
  )
})

StoryboardDesignerNode.displayName = 'StoryboardDesignerNode'
export default StoryboardDesignerNode
import { useNodePresentationWidth } from '../../NodePresentationContext'
